import { Toast } from '@innovationnorway/component-library';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SivaContractOverviewDataTable from '../components/SivaContractOverviewDataTable';
import { useSivaContracts } from '../hooks/use-siva-contracts';
import { ROUTES } from 'src/navigation';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'src/features/authorization';
import { useSivaOverviewTabs } from '../hooks/use-siva-overview-tabs';
import { useSivaContractDataExtract } from '../hooks/use-siva-contract-data-extract';
import saveAs from 'file-saver';
import DialogActions from 'src/components/DialogActions/DialogActions';
import DialogContainer from 'src/components/DialogContainer/DialogContainer';
import { HttpStatusCode, SivaContractOverviewDto, SivaPhase } from 'src/api/v2';
import { LoadingPage } from 'src/components/LoadingPage/LoadingPage';
import useCompanyContract from '../company-details/hooks/use-company-contract';
import confetti from 'canvas-confetti';
import '../components/confetti.scss';
import useSivaClusterDetails from '../../reporting/hooks/useSivaClusterDetails';
import useSivaReportingYear from 'src/hooks/use-siva-reporting-year';
import OpenMenuInfoButton from 'src/features/onboarding/components/OpenMenuInfoButton/OpenMenuInfoButton';
import { Button, Card, Chip, Field, Heading, Icon, Label, Select, Spinner } from '@innovationnorway/cl-react';
import useSivaYearList from 'src/hooks/use-year-list-reporting';
interface ToastProps {
  visible: boolean;
  header: string;
  text: string;
  type: 'success' | 'error' | undefined;
}
const SivaContractsIndexPage: React.FC = () => {
  const { t: tCommon } = useTranslation();
  const { t: tSiva } = useTranslation('siva');
  const { t: tPageTitles } = useTranslation('pageTitles');
  const navigate = useNavigate();
  const { checkAccess } = useAuth();
  const { read: isBusinessGarden } = checkAccess('UI_SivaBusinessGarden');
  const title = isBusinessGarden ? tPageTitles('targetedCompanies') : tSiva('ideasAndCompanies.title');
  const year = useSivaReportingYear();
  const {
    sivaClusterDetails,
    reportingDoneMutation,
    isLoading: isSivaClusterDetailsLoading,
  } = useSivaClusterDetails(year || 0, false);
  const [selectedYear, setSelectedYear] = useState<string | undefined>(undefined);
  const yearList = useSivaYearList(2023);
  const { mutations, contracts, isLoading } = useSivaContracts();
  const { downloadAllData, downloadKtuData } = useSivaContractDataExtract(
    selectedYear === '' ? undefined : parseInt(selectedYear || ''),
  );
  const [showDialog, setShowDialog] = useState(false);
  const [selectedRows, setSelectedRows] = useState<SivaContractOverviewDto[]>([]);
  const { availablePhasesForTab, tabElements, columnsForTab, activeTabName } =
    useSivaOverviewTabs(isBusinessGarden);
  const { revertSivaContractMutation } = useCompanyContract(undefined);
  const getToastProps = (toastType?: 'error' | 'successTerminate' | 'successReopen'): ToastProps => {
    switch (toastType) {
      case 'error':
        return {
          header: tSiva('ideasAndCompanies.generalErrorHeader'),
          text: tSiva('ideasAndCompanies.generalErrorMessage'),
          type: 'error',
          visible: true,
        };
      case 'successReopen':
        return {
          header:
            selectedRows.length > 1
              ? tSiva('ideasAndCompanies.ideasCompaniesReopenedPlural', { count: selectedRows.length })
              : tSiva('ideasAndCompanies.ideasCompaniesReopenedSingular', { count: selectedRows.length }),
          text: '',
          type: 'success',
          visible: true,
        };
      case 'successTerminate':
        return {
          header:
            selectedRows.length > 1
              ? tSiva('ideasAndCompanies.ideasTerminatedPlural.header', { count: selectedRows.length })
              : tSiva('ideasAndCompanies.ideasTerminatedSingular.header', { count: selectedRows.length }),
          text:
            selectedRows.length > 1
              ? tSiva('ideasAndCompanies.ideasTerminatedPlural.text')
              : tSiva('ideasAndCompanies.ideasTerminatedSingular.text'),
          visible: true,
          type: 'success',
        };
      default:
        return {
          header: '',
          text: '',
          visible: false,
          type: undefined,
        };
    }
  };
  const [toastProps, setToastProps] = useState<ToastProps>(getToastProps());
  const batchTerminateContracts = async () => {
    setShowDialog(false);
    setToastProps(getToastProps());
    const numericIds = selectedRows.map((row) => row.numericId);
    const phase = SivaPhase.Alumni;
    const fromDate = new Date();
    await mutations.batchUpdatePhase
      .mutateAsync({ numericIds, phase, fromDate })
      .catch(() => setToastProps(getToastProps('error')))
      .then((operationResults) => {
        if (operationResults != null && operationResults.some((or) => or.status != HttpStatusCode.OK)) {
          setToastProps(getToastProps('error'));
          return;
        }
        setToastProps(getToastProps('successTerminate'));
      });
  };
  const batchReopenContracts = async () => {
    setShowDialog(false);
    setToastProps(getToastProps());
    const numericIds = selectedRows.map((row) => row.numericId);
    const promises: Promise<void>[] = [];
    for (const numericId of numericIds) {
      promises.push(revertSivaContractMutation.mutateAsync(numericId));
    }
    Promise.all(promises).then(() => setToastProps(getToastProps('successReopen')));
  };
  const TerminateContractsDialog = () => {
    return (
      <DialogContainer
        show={showDialog}
        onClose={() => setShowDialog(false)}
        title={tSiva('ideasAndCompanies.terminateChosenIdeas')}
      >
        <p>
          {`${tSiva('ideasAndCompanies.wantToTerminate')} ${selectedRows.length} ${selectedRows.length > 1 ? tSiva('ideasAndCompanies.chosenIdeasPlural') : tSiva('ideasAndCompanies.chosenIdeasSingular')}`}
        </p>
        <DialogActions>
          <Button onClick={batchTerminateContracts}>{tSiva('ideasAndCompanies.terminate')}</Button>
          <Button variant="secondary" onClick={() => setShowDialog(false)}>
            {tCommon('cancel')}
          </Button>
        </DialogActions>
      </DialogContainer>
    );
  };
  const ReopenContractsDialog = () => {
    return (
      <DialogContainer
        show={showDialog}
        onClose={() => setShowDialog(false)}
        title={tSiva('ideasAndCompanies.reopenChosenIdeasCompanies')}
      >
        <p>{`${tSiva('ideasAndCompanies.wantToReopen')} ${selectedRows.length} ${selectedRows.length > 1 ? tSiva('ideasAndCompanies.chosenIdeasCompaniesPlural') : tSiva('ideasAndCompanies.chosenIdeasCompaniesSingular')}`}</p>
        <DialogActions>
          <Button onClick={batchReopenContracts}>
            {tSiva('ideasAndCompanies.reopenChosenIdeasCompanies')}
          </Button>
          <Button variant="secondary" onClick={() => setShowDialog(false)}>
            {tCommon('cancel')}
          </Button>
        </DialogActions>
      </DialogContainer>
    );
  };
  if (mutations.batchUpdatePhase.isLoading || revertSivaContractMutation.isLoading) {
    return (
      <main className="full-width-layout">
        <Heading level={1} className="margin-bottom--6">
          {title}
        </Heading>
        <LoadingPage />
      </main>
    );
  }
  return (
    <main className="full-width-layout">
      <div className="flex gap--2 justify-content--space-between">
        <div>
          <Heading level={1} className="margin-bottom--6">
            {title} <OpenMenuInfoButton page={'index/commonQuestions/ideasAndCompanies'} />
          </Heading>

          {checkAccess('SivaCore').read && (
            <div className="display--flex gap--1 align-items--flex-end ">
              <Button
                type="button"
                onClick={() => {
                  navigate(ROUTES.SIVA.IDEAS_AND_COMPANIES.NEW);
                }}
              >
                <Icon prefix={'fasr'} iconName={'plus'} />
                {tCommon('addNew')}
              </Button>

              <Field>
                <Label>{tSiva('export.ideasAndCompanies.yearLabel')}</Label>
                <Select
                  name="year"
                  value={selectedYear ?? 'none'}
                  className="margin-left--12 margin-0-on-all-children"
                  onChange={(event) => setSelectedYear(event.target.value)}
                >
                  {selectedYear === undefined && (
                    <Select.Option value="none" disabled>
                      {tCommon('selectYear')}
                    </Select.Option>
                  )}
                  <Select.Option value="">{tCommon('allYears')}</Select.Option>
                  {yearList.map((year) => (
                    <Select.Option key={year} value={year.toString()}>
                      {year}
                    </Select.Option>
                  ))}
                </Select>
              </Field>
              <Button
                type="button"
                variant="tertiary"
                disabled={selectedYear === undefined || downloadAllData.isLoading}
                onClick={async () => {
                  const file = await downloadAllData.mutateAsync(false);
                  saveAs(file.data, file.fileName);
                }}
              >
                <Icon prefix="fasr" iconName="file-arrow-down" />
                {tSiva('export.ideasAndCompanies.exportButton')}
              </Button>
              <Button
                type="button"
                variant="tertiary"
                onClick={async () => {
                  const file = await downloadKtuData.mutateAsync(false);
                  saveAs(file.data, file.fileName);
                }}
              >
                <Icon prefix="fasr" iconName="file-arrow-down" />
                {tSiva('export.ktu.exportButton')}
              </Button>
            </div>
          )}
        </div>
        {year != null && (
          <Card className="confetti-container stack-medium">
            <Heading level={2}>
              {tSiva('ideasAndCompanies.reportingDone.header', { year })}{' '}
              <OpenMenuInfoButton smallIcon page="index/commonQuestions/sivaReporting" />
            </Heading>
            <p>{tSiva('ideasAndCompanies.reportingDone.text', { year })}</p>
            <canvas id="canvas"></canvas>
            {isSivaClusterDetailsLoading ? (
              <Spinner aria-label={tCommon('loading')} />
            ) : (
              <Chip.Checkbox
                checked={sivaClusterDetails?.reportingDone || false}
                onClick={() => {
                  const newValue = !sivaClusterDetails?.reportingDone;
                  if (newValue == true) {
                    const cf = confetti.create(document.getElementById('canvas'), {
                      resize: true,
                      useWorker: true,
                    });
                    cf({
                      particleCount: 500,
                      spread: 110,
                      angle: 90,
                      startVelocity: 50,
                      ticks: 1000,
                      gravity: 1,
                      drift: 0.3,
                      disableForReducedMotion: true,
                      origin: {
                        x: 0.75,
                        y: 1.5,
                      },
                      colors: ['#141528', '#2d2e6c', '#565fea', '#f0f9fa'],
                    });
                  }
                  reportingDoneMutation.mutateAsync({ year, reportingDone: newValue });
                }}
              >
                {tSiva('ideasAndCompanies.reportingDone.finished')}
              </Chip.Checkbox>
            )}
          </Card>
        )}
      </div>

      {tabElements}
      <>
        {contracts.length === 0 && !isLoading ? (
          <div className="display--flex flex-direction--column align-items--flex-start gap--3 margin-bottom--6">
            <div className="display--flex align-items--center gap--2">
              <Icon prefix="fasr" iconName="sparkles" className="font-size--40px" />
              <Icon prefix="fasr" iconName="building" className="font-size--40px" />
            </div>
            <Heading level={2} className="margin-y--0">
              {tSiva('ideasAndCompanies.notFound')}
            </Heading>
          </div>
        ) : (
          <SivaContractOverviewDataTable
            availablePhases={availablePhasesForTab}
            columns={columnsForTab}
            setShowDialog={setShowDialog}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            activeTabName={activeTabName}
          />
        )}
      </>

      {activeTabName == 'ideas' && <TerminateContractsDialog />}
      {activeTabName == 'alumni' && <ReopenContractsDialog />}
      <Toast
        visible={toastProps.visible}
        header={toastProps.header}
        icon={
          <Icon
            prefix="fasr"
            iconName="info-circle"
            className={`${toastProps.type == 'success' && 'color--in-green-200'} ${toastProps.type == 'error' && 'color--red'}`}
          />
        }
        position="bottom-center"
        text={toastProps.text}
      />
    </main>
  );
};
export default SivaContractsIndexPage;
